import { supabase } from '../lib/supabase';
export interface LinkedAddon {
  id: string;
  recommended: boolean;
  addon_id: string;
  addon_name: string;
  addon_price: number | null;
  picture_url: string | null;
  active: boolean;
}

/**
 * Get linked-addon metadata for a set of parent product types. Returns a
 * map keyed by addon_id so callers can rank/sort addons based on whether
 * they are linked to any of the given products and whether any of those
 * links are marked recommended.
 */
export async function getLinkedAddonsForProducts(productTypeIds: string[]) {
  try {
    if (productTypeIds.length === 0) {
      return { data: new Map<string, { recommended: boolean }>(), error: null };
    }

    const { data, error } = await supabase
      .from('product_addons')
      .select('addon_id, recommended')
      .in('product_type_id', productTypeIds);

    if (error) throw error;

    const map = new Map<string, { recommended: boolean }>();
    for (const row of (data || []) as Array<{ addon_id: string; recommended: boolean }>) {
      const existing = map.get(row.addon_id);
      // Addon linked to multiple parents: recommended if ANY link is recommended.
      map.set(row.addon_id, { recommended: (existing?.recommended ?? false) || !!row.recommended });
    }

    return { data: map, error: null };
  } catch (error: any) {
    console.error('Error fetching linked addons for products:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get all addons linked to a product
 */
export async function getLinkedAddons(productTypeId: string) {
  try {
    const { data, error } = await supabase
      .from('product_addons')
      .select(`
        id,
        recommended,
        addon_id,
        product_types!product_addons_addon_id_fkey (
          id,
          product_name,
          picture_url,
          active
        )
      `)
      .eq('product_type_id', productTypeId)
      .order('recommended', { ascending: false });

    if (error) throw error;

    // Get pricing for each addon
    const addonIds = (data || []).map((item: any) => item.addon_id);

    let pricingData: any[] = [];
    if (addonIds.length > 0) {
      const { data: pricing } = await supabase
        .from('pricing_rules')
        .select('product_type_id, price')
        .in('product_type_id', addonIds)
        .eq('featured', true)
        .order('display_order', { ascending: true });

      pricingData = pricing || [];
    }

    // Transform data
    const linkedAddons: LinkedAddon[] = (data || []).map((item: any) => {
      const addonInfo = item.product_types;
      const pricing = pricingData.find(p => p.product_type_id === item.addon_id);

      return {
        id: item.id,
        recommended: item.recommended,
        addon_id: item.addon_id,
        addon_name: addonInfo?.product_name || 'Unknown',
        addon_price: pricing?.price || null,
        picture_url: addonInfo?.picture_url || null,
        active: addonInfo?.active ?? true
      };
    });

    return { data: linkedAddons, error: null };
  } catch (error: any) {
    console.error('Error fetching linked addons:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Get available addon products (not yet linked to this product)
 * Only returns addons that are available at the product's location
 */
export async function getAvailableAddons(merchantId: string, productTypeId: string, locationId?: string) {
  try {
    // First get already linked addon IDs
    const { data: linkedData } = await supabase
      .from('product_addons')
      .select('addon_id')
      .eq('product_type_id', productTypeId);

    const excludeIds = (linkedData || []).map((item: any) => item.addon_id);

    // Get all addon products for this merchant (join with locations to filter by merchant_id)
    // Also fetch location_id and available_location_ids to filter by location
    let query = supabase
      .from('product_types')
      .select(`
        id,
        product_name,
        picture_url,
        active,
        location_id,
        available_location_ids,
        locations!inner(merchant_id)
      `)
      .eq('locations.merchant_id', merchantId)
      .eq('product_type', 'addon')
      .eq('active', true)
      .order('product_name', { ascending: true });

    if (excludeIds.length > 0) {
      query = query.not('id', 'in', `(${excludeIds.join(',')})`);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Filter addons by location availability
    // An addon is available if:
    // 1. It belongs to the same location as the product, OR
    // 2. The product's location is in the addon's available_location_ids
    let filteredData = data || [];
    if (locationId) {
      filteredData = filteredData.filter((addon: any) => {
        // Same location
        if (addon.location_id === locationId) return true;
        // Available at this location via available_location_ids
        if (Array.isArray(addon.available_location_ids) && addon.available_location_ids.includes(locationId)) {
          return true;
        }
        return false;
      });
    }

    // Get pricing for each addon
    const addonIds = filteredData.map((item: any) => item.id);

    let pricingData: any[] = [];
    if (addonIds.length > 0) {
      const { data: pricing } = await supabase
        .from('pricing_rules')
        .select('product_type_id, price')
        .in('product_type_id', addonIds)
        .eq('featured', true)
        .order('display_order', { ascending: true });

      pricingData = pricing || [];
    }

    // Transform data
    const availableAddons = filteredData.map((item: any) => {
      const pricing = pricingData.find(p => p.product_type_id === item.id);

      return {
        id: item.id,
        product_name: item.product_name,
        picture_url: item.picture_url,
        active: item.active,
        price: pricing?.price || null
      };
    });

    return { data: availableAddons, error: null };
  } catch (error: any) {
    console.error('Error fetching available addons:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Link an addon to a product
 */
export async function linkAddon(productTypeId: string, addonId: string, recommended: boolean = false) {
  try {
    const { data, error } = await supabase
      .from('product_addons')
      .insert({
        product_type_id: productTypeId,
        addon_id: addonId,
        recommended
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error: any) {
    console.error('Error linking addon:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Update recommended flag for addon relationship
 */
export async function updateAddonRecommended(relationshipId: string, recommended: boolean) {
  try {
    const { data, error } = await supabase
      .from('product_addons')
      .update({ recommended })
      .eq('id', relationshipId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error: any) {
    console.error('Error updating addon recommended flag:', error);
    return { data: null, error: error.message };
  }
}

/**
 * Unlink an addon from a product
 */
export async function unlinkAddon(relationshipId: string) {
  try {
    const { error } = await supabase
      .from('product_addons')
      .delete()
      .eq('id', relationshipId);

    if (error) throw error;

    return { error: null };
  } catch (error: any) {
    console.error('Error unlinking addon:', error);
    return { error: error.message };
  }
}

/**
 * Save all addon changes (add new, update existing, remove deleted)
 */
export async function saveAddonChanges(
  productTypeId: string,
  addons: Array<{
    id?: string;
    addon_id: string;
    recommended: boolean;
    _status: 'existing' | 'added' | 'removed';
  }>
) {
  try {
    const errors: string[] = [];

    // Process removed addons
    const toRemove = addons.filter(a => a._status === 'removed' && a.id);
    for (const addon of toRemove) {
      const { error } = await unlinkAddon(addon.id!);
      if (error) errors.push(`Failed to remove ${addon.addon_id}: ${error}`);
    }

    // Process added addons
    const toAdd = addons.filter(a => a._status === 'added');
    for (const addon of toAdd) {
      const { error } = await linkAddon(productTypeId, addon.addon_id, addon.recommended);
      if (error) errors.push(`Failed to add ${addon.addon_id}: ${error}`);
    }

    // Process updated existing addons (recommended flag changed)
    const toUpdate = addons.filter(a => a._status === 'existing' && a.id);
    for (const addon of toUpdate) {
      const { error } = await updateAddonRecommended(addon.id!, addon.recommended);
      if (error) errors.push(`Failed to update ${addon.addon_id}: ${error}`);
    }

    if (errors.length > 0) {
      return { error: errors.join('; ') };
    }

    return { error: null };
  } catch (error: any) {
    console.error('Error saving addon changes:', error);
    return { error: error.message };
  }
}
